import React, { useEffect, useState, useRef, useMemo } from 'react';
import { Box, Button, Divider, Grid, Typography } from '@mui/material';
import { connect } from 'react-redux';
import { useOutletContext } from 'react-router-dom';
import CheckIcon from '@mui/icons-material/Check';
import { useNavigate } from 'react-router-dom';
import darkBarcode from '../../assets/images/darkbarcode.png';
import scannerIcon from '../../assets/images/Bar_code_scan.png';
import ReportProblemRoundedIcon from '@mui/icons-material/ReportProblemRounded';
import CloseIcon from '@mui/icons-material/Close';

import {
  AllPartsModal,
  BasicTable,
  CurrentVsTotal,
  ScanOrEnterBarcodeForm,
} from '../../components';
import {
  DARK_MODE,
  OPERATIONS,
  PART_VALIDATION_ERROR,
  REDIRECT_URLS,
  isPuneJlr,
} from '../../constants';
import {
  bookSequenceNumber,
  raiseExceptionRequest,
  submitMarkUnprocessed,
  updatePartValidation,
  validatePart,
  revalidatePart,
} from '../../redux/actionCreators/book';
import { updateReceivePart } from '../../redux/actionCreators/receivePart';
import { pxToRem, responsiveFontSizes } from '../../utils/getFontValue';
import {
  useAutoFocus,
  useRedirectToNextScreen,
  useResolutionScale,
} from '../../hooks';
import { useMessageContextManager } from '../../components/MessageOverlay/MessageOverlay';
import { ERROR_MESSAGE } from '../../constants';
import { markComplete } from '../../redux/actionCreators/printBarcode';
import SkipPart from '../../components/SkipPart/SkipPart';
import TakeOut from '../../components/TakeOut/TakeOut';
import MoveToMr from '../../components/MoveToMr/MoveToMr';
import RescanModal from '../../components/ReportModal/RescanModal';
import { getPartFamiliesToValidate } from '../../utils/utils';
import StyledTable from '../../components/StyledTable/StyledTable';
import TableActionsBar, {
  GenealogyChip,
  GradientButton,
  AddDefectButton,
} from '../../components/TableActionBar/TableActionBar';
import EclipseIcon from '../../assets/icons/EclipseIcon';
import FilledCheckIcon from '../../assets/icons/CheckIcon';
import { useTorqueColumns } from '../../components/TorqueTable/useTorqueColumns';
import ReasonAndRemarkModal from '../../components/ReportModal/ReasonAndRemarkModal';
import { updateStatusFromScanJobDetails } from '../../redux/actionCreators/book';
import ExceptionModal from '../../components/ReportModal/ExceptionModal';
import styled from '@emotion/styled';
import { CarRepairRounded, Margin, SkipNext } from '@mui/icons-material';
import CurlyCheckIcon from '../../assets/icons/CurlyCheckIcon';
import styles from './BookingValidation.module.scss';
import clsx from 'clsx';
import { getProcessValidationDetailsForSerialNumber } from '../../redux/actionCreators/processValidation';
import InfoIcon from '../../assets/icons/InfoIcon';
import ValidPartsDetailsPopover from '../ProcessValidation/ValidPartsDetailsPopover';
import BookingInfoPopup from './BookingInfoPopup';
import { toast } from 'react-toastify';
import DefectModal from '../../components/ReportModal/DefectModal';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------
const SUCCESS_DISMISS_SEC = 10;

const getResponsiveRowHeight = (isAssemblyLine) => (isAssemblyLine ? 120 : 60);

const INTIAL_PART_FAMILY_SCAN_DATA = {
  scannedPartNumber: '',
  isPartNumberScanningDone: false,
  isErrorInScanningPartNumber: false,
  scannedSerialNumber: '',
  isSerialNumberScanningDone: false,
  isErrorInScanningSerialNumber: false,
};

const classNames = {
  subtitles: { fontSize: '30px' },
  paper: { height: '100%', display: 'flex', flexDirection: 'column' },
  tableRow: { verticalAlign: 'top' },
  tableContainer: { height: 3 * 38 },
  tableContentContainer: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    marginTop: '10px',
    borderBottomLeftRadius: '5px',
    borderBottomRightRadius: '5px',
  },
  heading: {
    fontSize: '20px',
    fontWeight: 600,
    ...responsiveFontSizes({ sm: 12, md: 20, lg: 20 }),
  },
  text: {
    display: 'flex',
    justifyContent: 'space-between',
    letterSpacing: 0.5,
    overflow: 'auto',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    fontWeight: 'normal',
    fontSize: pxToRem(16),
    ...responsiveFontSizes({ sm: 16, md: 18, lg: 20 }),
  },
  enterSerialNumber: {
    color: '#3070db',
    paddingLeft: 1,
    fontWeight: 700,
    lineHeight: 1.5,
    fontSize: pxToRem(14),
    ...responsiveFontSizes({ sm: 14, md: 16, lg: 24 }),
    '&:hover': { backgroundColor: 'transparent' },
  },
  textInput: {
    borderRadius: 2,
    background: ({ palette }) =>
      palette.mode === DARK_MODE ? 'white' : palette.background.paper,
    paddingLeft: 2,
  },
  buttonStyles: {
    marginRight: 0,
    px: 4,
    background: '#82C1A7',
    borderRadius: 1,
    color: 'white',
    width: 70,
    '&.Mui-disabled': {
      color: 'rgba(0, 0, 0, 0.26)',
      backgroundColor: '#d8d8d8',
    },
  },
  cell: { borderBottom: 'none', paddingBottom: 0 },
};

const getResponsiveClassNames = (isAssemblyLine) => {
  const rowHeight = getResponsiveRowHeight(isAssemblyLine);
  return {
    subtitles: { fontSize: isAssemblyLine ? '60px' : '30px' },
    paper: { height: '100%', display: 'flex', flexDirection: 'column' },
    tableRow: { verticalAlign: 'top' },
    tableContainer: { height: 3 * rowHeight },
    tableContentContainer: {
      display: 'flex',
      flexDirection: 'column',
      flex: 1,
      marginTop: isAssemblyLine ? '20px' : '10px',
      borderBottomLeftRadius: '5px',
      borderBottomRightRadius: '5px',
    },
    heading: {
      fontSize: isAssemblyLine ? '40px' : '20px',
      fontWeight: 600,
      ...responsiveFontSizes({ sm: 12, md: 20, lg: isAssemblyLine ? 40 : 20 }),
    },
    text: {
      display: 'flex',
      justifyContent: 'space-between',
      letterSpacing: 0.5,
      overflow: 'auto',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      fontWeight: 'normal',
      fontSize: pxToRem(isAssemblyLine ? 32 : 16),
      ...responsiveFontSizes({ sm: 16, md: 18, lg: isAssemblyLine ? 32 : 20 }),
    },
    enterSerialNumber: {
      color: '#3070db',
      paddingLeft: 1,
      fontWeight: 700,
      lineHeight: 1.5,
      fontSize: pxToRem(isAssemblyLine ? 28 : 14),
      ...responsiveFontSizes({ sm: 14, md: 16, lg: isAssemblyLine ? 28 : 24 }),
      '&:hover': { backgroundColor: 'transparent' },
    },
    textInput: {
      borderRadius: 2,
      background: ({ palette }) =>
        palette.mode === DARK_MODE ? 'white' : palette.background.paper,
      paddingLeft: 2,
    },
    buttonStyles: { marginRight: 0, px: 4, background: '#82C1A7' },
  };
};

// ---------------------------------------------------------------------------
// Redux wiring
// ---------------------------------------------------------------------------
const mapStateToProps = (state) => ({
  receivePart: state.receivePart,
  partValidation: state.partValidation,
  themeMode: state.theme.themeMode,
  isMobileStation: state.user.isMobileStation,
  currentLineItem: state.station.currentLineItem,
  modeType: state.modeType,
});

const mapDispatchToProps = (dispatch) => ({
  bookSequenceNumber: (sequenceNumber, successCallback, errorCallback) =>
    dispatch(bookSequenceNumber(sequenceNumber, successCallback, errorCallback)),
  submitMarkUnprocessed: (sequenceNumber, payload, successCallback, errorCallback) =>
    dispatch(submitMarkUnprocessed(sequenceNumber, payload, successCallback, errorCallback)),
  raiseExceptionRequest: (asn, payload, successCallback, errorCallback) =>
    dispatch(raiseExceptionRequest(asn, payload, successCallback, errorCallback)),
  validatePart: (sequenceNumber, payload, successCallback, errorCallback) =>
    dispatch(validatePart(sequenceNumber, payload, successCallback, errorCallback)),
  revalidatePart: (sequenceNumber, idToUpdate, payload, successCallback, errorCallback) =>
    dispatch(revalidatePart(sequenceNumber, idToUpdate, payload, successCallback, errorCallback)),
  updatePartValidation: (data) => dispatch(updatePartValidation(data)),
  updateReceivePart: (data) => dispatch(updateReceivePart(data)),
  markComplete: (successCallback, errorCallback, sequenceNumber) =>
    dispatch(markComplete(successCallback, errorCallback, sequenceNumber)),
  getProcessValidationDetailsForSerialNumber: (serialNumber, successCallback, errorCallback) =>
    dispatch(getProcessValidationDetailsForSerialNumber(serialNumber, successCallback, errorCallback)),
});

// ---------------------------------------------------------------------------
// Column definitions
// ---------------------------------------------------------------------------
const PVnG_DEFAULT_COLUMNS = [
  { width: 250, title: 'Part Family', field: 'familyName' },
  {
    width: 250,
    title: 'Part Number',
    field: 'partNumber',
    render: (prop, { openEllipsisPopupForEventWithText: handlePopoverOpen, closeEllipsisPopup: handlePopoverClose, onCopy }) => (
      <Box sx={{ display: 'flex' }}>
        <Typography
          sx={{
            whiteSpace: 'nowrap',
            width: '90%',
            textAlign: 'left',
            letterSpacing: 0.5,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            fontSize: pxToRem(15),
            ...responsiveFontSizes({ sm: 15, md: 17, lg: 21 }),
            opacity: !prop.hasOwnProperty('isErrorInScanningPartFamily') ? 0.8 : 1,
            color: !prop.hasOwnProperty('isErrorInScanningPartFamily')
              ? null
              : prop.isErrorInScanningPartFamily ? '#F17C7C' : '#82C1A7',
          }}
          onMouseEnter={(e) => handlePopoverOpen(e, prop.partNumber)}
          onMouseLeave={() => handlePopoverClose()}
          onDoubleClick={() => onCopy(prop.partNumber)}
        >
          {prop.partNumber}
        </Typography>
      </Box>
    ),
  },
  {
    width: 300,
    title: 'Part Description',
    field: 'partDescription',
    render: (prop, { openEllipsisPopupForEventWithText: handlePopoverOpen, closeEllipsisPopup: handlePopoverClose, onCopy }) => (
      <Typography
        sx={{
          letterSpacing: 0.5,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          fontSize: pxToRem(15),
          ...responsiveFontSizes({ sm: 15, md: 17, lg: 21 }),
          opacity: !prop.hasOwnProperty('isErrorInScanningPartFamily') ? 0.8 : 1,
          color: !prop.hasOwnProperty('isErrorInScanningPartFamily')
            ? null
            : prop.isErrorInScanningPartFamily ? '#F17C7C' : '#82C1A7',
        }}
        onMouseEnter={(e) => handlePopoverOpen(e, prop.partDescription)}
        onMouseLeave={() => handlePopoverClose()}
        onDoubleClick={() => onCopy(prop.partDescription)}
      >
        {prop.partDescription}
      </Typography>
    ),
  },
  {
    width: 150,
    title: 'Quantity',
    field: 'quantity',
    render: (prop) => {
      const scanJobResults = prop.scanJobResults || [];
      const totalQuantity = prop.quantity || 0;
      const validatedCount = scanJobResults.filter((r) => !r.skipReason).length;
      const skippedCount = scanJobResults.filter((r) => r.skipReason).length;
      const completedCount = validatedCount + skippedCount;
      const remainingCount = totalQuantity - completedCount;
      return (
        <CurrentVsTotal
          classNames={{
            currentNumber: { fontWeight: 700, fontSize: pxToRem(16), ...responsiveFontSizes({ sm: 16, md: 18, lg: 24 }) },
            totalNumber: { fontSize: pxToRem(13), ...responsiveFontSizes({ sm: 13, md: 15, lg: 19 }) },
          }}
          currentNumber={remainingCount}
          totalNumber={totalQuantity}
        />
      );
    },
  },
  {
    width: 250,
    title: 'Serial Number',
    field: 'displaySerialNumber',
    render: (prop, { openEllipsisPopupForEventWithText: handlePopoverOpen, closeEllipsisPopup: handlePopoverClose, onCopy }) => (
      <Typography
        sx={{
          letterSpacing: 0.5,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          fontSize: pxToRem(15),
          ...responsiveFontSizes({ sm: 15, md: 17, lg: 21 }),
          opacity: !prop.skipReason && (prop.displaySerialNumber || prop.scannedSerialNumber) ? 0.8 : 1,
          color:
            !prop.hasOwnProperty('isErrorInScanningPartFamily') &&
              !prop.skipReason &&
              (prop.displaySerialNumber || prop.scannedSerialNumber)
              ? '#82C1A7'
              : '#F17C7C',
        }}
        onMouseEnter={(e) => handlePopoverOpen(e, prop.displaySerialNumber || prop.scannedSerialNumber)}
        onMouseLeave={() => handlePopoverClose()}
        onDoubleClick={() => onCopy(prop.displaySerialNumber || prop.scannedSerialNumber)}
      >
        {prop.displaySerialNumber || prop.scannedSerialNumber || '-'}
      </Typography>
    ),
  },
  {
    width: 250,
    title: 'Part Validation & Genealogy',
    field: 'partValidation&Genealogy',
    render: ({ isSelected, skipReason, scannedSerialNumber, scannedPartNumber }) => (
      <Grid item display="flex">
        {(scannedSerialNumber || scannedPartNumber) && !skipReason ? (
          <CheckIcon sx={{ color: '#82C1A7' }} />
        ) : (
          <ReportProblemRoundedIcon color="error" />
        )}
        <Typography
          component="span"
          sx={{
            paddingLeft: 1,
            letterSpacing: 0.5,
            fontWeight: isSelected ? 700 : 500,
            fontSize: pxToRem(15),
            ...responsiveFontSizes({ sm: 15, md: 17, lg: 21 }),
            color:
              (scannedSerialNumber || scannedPartNumber) && !skipReason ? '#82C1A7' : '#e47778',
          }}
        >
          {(scannedSerialNumber || scannedPartNumber) && !skipReason
            ? 'Validated'
            : skipReason
              ? 'Skipped'
              : 'Pending'}
        </Typography>
      </Grid>
    ),
  },
  {
    width: 250,
    title: 'Reason to Skip',
    field: 'skipReason',
    columnStyle: { color: '#F17C7C' },
  },
];

// ---------------------------------------------------------------------------
// Sub-component: UpdatePartValidationGenealogy (Manual Mode scanning UI)
// ---------------------------------------------------------------------------
const UpdatePartValidationGenealogy = ({
  partValidation,
  selectedRows,
  themeMode,
  tableRows,
  setTableRows,
  revalidatePart,
  validatePart,
  setAllScanned,
  isAssemblyLine,
}) => {
  const { isPopupOpen, showMessageOverlay } = useMessageContextManager();
  const [partFamilyScannedData, setPartFamilyScannedData] = useState(INTIAL_PART_FAMILY_SCAN_DATA);
  const [skippedSerialNumber, setSkippedSerialNumber] = useState('');
  const [skippedPartNumber, setSkippedPartNumber] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const { scanJobDetails } = partValidation;
  const partNumberInputRef = useRef(null);
  const serialNumberInputRef = useRef(null);

  const onPartNumberSubmit = (scannedValue, resetBarcodeValue) => {
    const {
      validParts, familyId, partNumber, skipReason,
      partNumberScanningRequired, genealogyRequired, partPosition, scannedPartNumber,
    } = selectedRows[0];
    const sequenceNumber = partValidation.sequenceNumber;

    let scanJobResultId = selectedRows[0]?.scanJobResultId;
    if (!scanJobResultId && selectedRows[0]?.scanJobResults?.length > 0) {
      scanJobResultId = selectedRows[0].scanJobResults[selectedRows[0].scanJobResults.length - 1]?.id;
    }

    const successCallback = (data) => {
      closeMesssageOverlay();
      setSuccessMessage('Part Matched');
      setPartFamilyScannedData({
        ...partFamilyScannedData,
        scannedPartNumber: scannedValue,
        isPartNumberScanningDone: true,
        isErrorInScanningPartNumber: false,
      });
      if (data?.scanJobDetails) {
        setTableRows(getPartFamiliesToValidate(data.scanJobDetails, data.sequenceNumber));
      }
      setTimeout(() => { setSuccessMessage(''); resetBarcodeValue(); }, 3000);
    };

    const errorCallback = () => {
      const rowsData = tableRows.map((pf) =>
        pf.familyId === familyId && pf.partPosition === partPosition
          ? { ...pf, scannedValue, skippedPartNumber: scannedValue, isErrorInScanningPartFamily: true }
          : { ...pf }
      );
      setTableRows(rowsData);
      closeMesssageOverlay();
      setSkippedPartNumber(scannedValue);
      setPartFamilyScannedData((prev) => ({ ...prev, isPartNumberScanningDone: false, isErrorInScanningPartNumber: true }));
      resetBarcodeValue();
      showMessageOverlay({
        contextText: PART_VALIDATION_ERROR.ERROR_CONTEXT_TEXT + 'Scanning Part Number',
        info: 'Part did not match',
        error: true,
      });
    };

    const closeMesssageOverlay = showMessageOverlay({ contextText: 'Scanning Part Number.', loading: true });
    setPartFamilyScannedData((prev) => ({ ...prev, scannedPartNumber: scannedValue }));

    const payload = { familyId, partNumber, scannedPartNumber: scannedValue, scannedSerialNumber: null };
    const checkAllScanned = () => {
      tableRows.forEach((item) => {
        if (item?.quantity === item?.scanJobResults?.length) setAllScanned(true);
        else setAllScanned(false);
      });
    };

    if (genealogyRequired && partNumberScanningRequired) {
      validatePart(sequenceNumber, payload, successCallback, errorCallback);
    } else if (partNumberScanningRequired) {
      const hasScanJobResults = selectedRows[0]?.scanJobResults?.length > 0;
      const hasExistingScan = scanJobResultId || scannedPartNumber || skipReason || hasScanJobResults;
      const alternativeDetection =
        selectedRows[0]?.scannedPartNumber || selectedRows[0]?.scannedSerialNumber ||
        selectedRows[0]?.skipReason || selectedRows[0]?.displaySerialNumber || hasScanJobResults;
      const hasValidScanJobResultId = scanJobResultId && scanJobResultId !== 'undefined' && scanJobResultId !== null;

      if ((hasExistingScan || alternativeDetection) && hasValidScanJobResultId) {
        revalidatePart(sequenceNumber, scanJobResultId, payload, (data) => { successCallback(data); checkAllScanned(); }, errorCallback);
      } else {
        validatePart(sequenceNumber, payload, (data) => { successCallback({}); checkAllScanned(); }, errorCallback);
      }
    }
  };

  const onSerialNumberSubmit = (scannedValue, resetBarcodeValue) => {
    const {
      sequenceNumber, familyId, partNumber, scanJobResultId: rowScanJobResultId,
      skipReason, partNumberScanningRequired, genealogyRequired, partPosition, scannedSerialNumber,
    } = selectedRows[0];

    let scanJobResultId = rowScanJobResultId || selectedRows[0]?.scanJobResultId;
    if (!scanJobResultId && selectedRows[0]?.scanJobResults?.length > 0) {
      scanJobResultId = selectedRows[0].scanJobResults[selectedRows[0].scanJobResults.length - 1]?.id;
    }

    const successCallback = () => {
      closeMesssageOverlay();
      setSuccessMessage('Serial Number Matched');
      setPartFamilyScannedData(INTIAL_PART_FAMILY_SCAN_DATA);
      setTimeout(() => { setSuccessMessage(''); resetBarcodeValue(); }, 3000);
    };

    const errorCallback = (error) => {
      const rowsData = tableRows.map((pf) =>
        pf.familyId === familyId && pf.partPosition === partPosition
          ? { ...pf, scannedValue, skippedSerialNumber: scannedValue, displaySerialNumber: scannedValue, isErrorInScanningPartFamily: true }
          : { ...pf }
      );
      setTableRows(rowsData);
      setSkippedSerialNumber(scannedValue);
      closeMesssageOverlay();
      setPartFamilyScannedData((prev) => ({ ...prev, isSerialNumberScanningDone: false, isErrorInScanningSerialNumber: true }));
      resetBarcodeValue();
      showMessageOverlay({
        contextText: PART_VALIDATION_ERROR.ERROR_CONTEXT_TEXT + 'Scanning Serial Number',
        info: error.response.data.message,
        error: true,
      });
    };

    const closeMesssageOverlay = showMessageOverlay({ contextText: 'Scanning Serial Number.', loading: true });
    setPartFamilyScannedData((prev) => ({ ...prev, scannedSerialNumber: scannedValue }));

    let scannedPartNumber;
    if (genealogyRequired && !partNumberScanningRequired) scannedPartNumber = '';
    else if (genealogyRequired && partNumberScanningRequired) scannedPartNumber = partNumber;
    else scannedPartNumber = partFamilyScannedData.scannedPartNumber;

    const payload = { familyId, partNumber, scannedPartNumber, scannedSerialNumber: scannedValue };
    const hasScanJobResults = selectedRows[0]?.scanJobResults?.length > 0;
    const isPartProcessed =
      scanJobResultId || scannedSerialNumber || skipReason ||
      selectedRows[0]?.scannedPartNumber || selectedRows[0]?.scannedSerialNumber ||
      selectedRows[0]?.skipReason || selectedRows[0]?.displaySerialNumber || hasScanJobResults;
    const hasValidScanJobResultId = scanJobResultId && scanJobResultId !== 'undefined' && scanJobResultId !== null;

    const checkAllScanned = () => {
      tableRows.forEach((item) => {
        if (item?.quantity === item?.scanJobResults?.length) setAllScanned(true);
        else setAllScanned(false);
      });
    };

    if (isPartProcessed && hasValidScanJobResultId) {
      revalidatePart(sequenceNumber, scanJobResultId, payload, (data) => { successCallback(data); checkAllScanned(); }, errorCallback);
    } else {
      validatePart(sequenceNumber, payload, (data) => { successCallback(data); checkAllScanned(); }, errorCallback);
    }
  };

  const isSkipDisabled = () => {
    const selectedRow = selectedRows[0];
    const latestPartToSkip = tableRows.find(
      (row) => row.familyId === selectedRow.familyId && !(row.scannedPartNumber || row.scannedSerialNumber || row.skipReason)
    );
    return selectedRow.partPosition !== latestPartToSkip?.partPosition;
  };

  const partSkipSuccessCallback = (data, closeSkipSerialNumberDialog) => {
    const { scanJobDetails, sequenceNumber } = data;
    setTableRows(getPartFamiliesToValidate(scanJobDetails, sequenceNumber));
    closeSkipSerialNumberDialog();
    toast.success('Part Skipped');
    setPartFamilyScannedData(INTIAL_PART_FAMILY_SCAN_DATA);
  };

  const partSkipErrorCallback = (error, closeSkipSerialNumberDialog) => {
    closeSkipSerialNumberDialog();
    showMessageOverlay({
      contextText: PART_VALIDATION_ERROR.ERROR_CONTEXT_TEXT + 'Skipping Part Family',
      info: error.response.data.message,
      error: true,
    });
  };

  const renderInputFields = () => {
    const { partNumberScanningRequired, genealogyRequired, isErrorInScanningPartFamily } = selectedRows[0];

    if (partNumberScanningRequired && !partFamilyScannedData.isPartNumberScanningDone)
      return (
        <Grid item container alignItems="end" columnSpacing={2} sx={{ opacity: selectedRows.length === 1 ? '100%' : '50%', height: '100vh' }}>
          <Grid item display="flex" alignItems="center">
            {isErrorInScanningPartFamily && partFamilyScannedData.isErrorInScanningPartNumber ? (
              <Typography variant="h3" color="error">RESCAN PART NO.</Typography>
            ) : (
              <Typography variant="h3" sx={{ color: '#27C8F7' }}>SCAN PART NO.</Typography>
            )}
            <Box item sx={{ height: isAssemblyLine ? 120 : { xs: 35, md: 40, lg: 60 }, width: isAssemblyLine ? 260 : { xs: 75, md: 85, lg: 130 } }}>
              <img src={themeMode === DARK_MODE ? darkBarcode : scannerIcon} alt="" style={{ height: '100%', width: '100%', objectFit: 'cover' }} />
            </Box>
          </Grid>
          <Grid item xs={12} sx={{ width: { sm: '75%', md: '60%' }, height: '100%' }} pt={1}>
            <ScanOrEnterBarcodeForm
              enableAutoEnter={genealogyRequired && partNumberScanningRequired ? false : true}
              inputRef={partNumberInputRef}
              inputProps={{ disabled: selectedRows.length !== 1 }}
              onSubmit={onPartNumberSubmit}
              placeholder="Part / Serial Number Scan"
            />
          </Grid>
        </Grid>
      );

    if (genealogyRequired && !partFamilyScannedData.isSerialNumberScanningDone)
      return (
        <Grid item container direction="column" alignItems="end" columnSpacing={2} sx={{ opacity: selectedRows.length === 1 ? '100%' : '50%', height: '100%' }}>
          <Grid item display="flex" alignItems="center">
            {isErrorInScanningPartFamily && partFamilyScannedData.isErrorInScanningSerialNumber ? (
              <Typography variant="h3" color="error">RESCAN SERIAL NUMBER</Typography>
            ) : (
              <Typography variant="h3" sx={{ color: '#27C8F7' }}>SCAN SERIAL NUMBER</Typography>
            )}
            <Box item sx={{ height: isAssemblyLine ? 120 : { xs: 35, md: 40, lg: 60 }, width: isAssemblyLine ? 260 : { xs: 75, md: 85, lg: 130 } }} />
          </Grid>
          <Grid item xs={12} sx={{ width: { sm: '75%', md: '60%', height: '100%' } }} pt={1}>
            <ScanOrEnterBarcodeForm
              enableAutoEnter={genealogyRequired && partNumberScanningRequired ? false : true}
              inputRef={serialNumberInputRef}
              inputProps={{ disabled: selectedRows.length !== 1 }}
              onSubmit={onSerialNumberSubmit}
              placeholder="Part / Serial Number Scan"
            />
          </Grid>
        </Grid>
      );
  };

  useEffect(() => {
    if (!isPopupOpen) {
      if (partNumberInputRef.current) partNumberInputRef.current.focus();
      if (serialNumberInputRef.current) serialNumberInputRef.current.focus();
    }
  }, [selectedRows, isPopupOpen]);

  return (
    <>
      <Grid item xs={5} display="flex" flexWrap="wrap" alignItems="flex-end">
        <Grid item pt={1}>
          {!successMessage && selectedRows.length === 1 &&
            !(selectedRows[0]?.skipReason || selectedRows[0]?.scannedSerialNumber || selectedRows[0]?.scannedPartNumber) && (
              <SkipPart
                skippedPartNumber={selectedRows[0].skippedPartNumber}
                skippedSerialNumber={selectedRows[0].skippedSerialNumber}
                disableBtn={isSkipDisabled()}
                hoverMessage="Please select the quantity in sequential manner"
                successCallback={partSkipSuccessCallback}
                errorCallback={partSkipErrorCallback}
                currentPartNumber={scanJobDetails.findIndex((part) => part.familyId === selectedRows[0]?.familyId)}
              />
            )}
        </Grid>
        {successMessage && (
          <>
            <CheckIcon color="primary" sx={{ fontSize: { lg: 34 } }} />
            <Typography variant="h4" component="span" color="primary" fontWeight={400} paddingLeft={1}>
              {successMessage}
            </Typography>
          </>
        )}
      </Grid>
      <Grid item xs={7}>
        {!successMessage && !!selectedRows.length && renderInputFields()}
      </Grid>
    </>
  );
};

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------
export const BookingValidationStages = ({
  bookSequenceNumber,
  submitMarkUnprocessed,
  raiseExceptionRequest,
  receivePart,
  partValidation,
  themeMode,
  validatePart,
  revalidatePart,
  updatePartValidation,
  updateReceivePart,
  isMobileStation,
  currentLineItem,
  modeType,
  getProcessValidationDetailsForSerialNumber,
}) => {
  const { scaleValue } = useResolutionScale();
  const { handleNext } = useOutletContext();
  const [allScanned, setAllScanned] = useState(false);
  const redirect = useRedirectToNextScreen();
  const navigate = useNavigate();

  const [currentPartNumber, setCurrentPartNumber] = useState(null);
  const [selectedPvRows, setSelectedPvRows] = useState([]);
  const { is4K, isAssemblyLine, getResponsiveValue } = useResolutionScale();

  const { inputRef } = useAutoFocus({
    timeout: 10000,
    enabled: true,
    componentId: 'scan-vin-input',
    onFocus: () => { },
  });

  const { scanJobDetails = [] } = partValidation;
  const {
    familyId, partNumber, quantity,
    scanJobResults = [], validParts,
    genealogyRequired, partNumberScanningRequired,
  } = scanJobDetails[currentPartNumber] || {};

  const responsiveClassNames = getResponsiveClassNames(isAssemblyLine);
  const rowHeight = getResponsiveRowHeight(isAssemblyLine);

  const [isDetailsModalOpen, setIsDetailModalOpen] = useState(false);
  const [isReportModalOpen, setIsReportModalOpen] = useState(false);
  const [partFamilyScannedData, setPartFamilyScannedData] = useState(INTIAL_PART_FAMILY_SCAN_DATA);
  const [skippedSerialNumber, setSkippedSerialNumber] = useState('');
  const [skippedPartNumber, setSkippedPartNumber] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [tableRows, setTableRows] = useState([]);
  const [noPartFamilyConfigured, setNoPartFamilyConfigured] = useState(false);

  const { isPopupOpen, showMessageOverlay } = useMessageContextManager();

  // ── Booking modal state ───────────────────────────────────────────────────
  const [isBookingConfirmOpen, setIsBookingConfirmOpen] = useState(false);
  // 'idle' | 'success'
  const [bookingPhase, setBookingPhase] = useState('idle');
  const [successLeft, setSuccessLeft] = useState(SUCCESS_DISMISS_SEC);
  const successTimerRef = useRef(null);
  // ─────────────────────────────────────────────────────────────────────────

  const closeDetailsModal = () => { if (inputRef.current) inputRef.current.focus(); setIsDetailModalOpen(false); };
  const openReportModal = () => setIsReportModalOpen(true);
  const closeReportModal = () => setIsReportModalOpen(false);
  const [isDefectModalOpen, setIsDefectModalOpen] = useState(false);
  const openDefectModal = () => setIsDefectModalOpen(true);
  const closeDefectModal = () => setIsDefectModalOpen(false);
  const [isTakeOutModalOpen, setIsTakeOutModalOpen] = useState(false);
  const openTakeOutModal = () => setIsTakeOutModalOpen(true);
  const closeTakeOutModal = () => setIsTakeOutModalOpen(false);

  const redirectToNextScreen = (scanJobDetails) => {
    const nonScannedIdx = scanJobDetails.findIndex((sj) => sj.scanJobResults.length !== sj.quantity);
    if (nonScannedIdx === -1) setCurrentPartNumber(scanJobDetails.length - 1);
    else setCurrentPartNumber(nonScannedIdx);
  };

  const partSkipSuccessCallback = (data, closeSkipSerialNumberDialog) => {
    closeSkipSerialNumberDialog();
    toast.success('Part Skipped');
    setPartFamilyScannedData(INTIAL_PART_FAMILY_SCAN_DATA);
  };

  const partSkipErrorCallback = (error, closeSkipSerialNumberDialog) => {
    closeSkipSerialNumberDialog();
    setSkippedSerialNumber(partFamilyScannedData.scannedSerialNumber);
    showMessageOverlay({
      contextText: PART_VALIDATION_ERROR.ERROR_CONTEXT_TEXT + 'Skipping Part Family',
      info: error.response.data.message,
      error: true,
    });
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      clearInterval(successTimerRef.current);
    };
  }, []);

  const redirectToHomeScreen = () => {
    const prefix = window.location.pathname.startsWith('/pune-jlr') ? '/pune-jlr' : '';
    window.location.replace(`${prefix}${REDIRECT_URLS[OPERATIONS.RECEIVE_PART]}`);
  };
  // ── Direct booking — no countdown, no loader ──────────────────────────────
  const handleBookingConfirm = () => {
    setIsBookingConfirmOpen(false);

    bookSequenceNumber(
      partValidation.sequenceNumber,
      () => {
        // Success → show success state with 10s auto-dismiss
        setBookingPhase('success');
        setSuccessLeft(SUCCESS_DISMISS_SEC);

        let sElapsed = 0;
        successTimerRef.current = setInterval(() => {
          sElapsed += 1;
          setSuccessLeft(SUCCESS_DISMISS_SEC - sElapsed);
          if (sElapsed >= SUCCESS_DISMISS_SEC) {
            clearInterval(successTimerRef.current);
            setBookingPhase('idle');
            redirectToHomeScreen();
          }
        }, 1000);
      },
      (error) => {
        setBookingPhase('idle');
        showMessageOverlay({
          contextText: 'Booking Failed',
          info: error?.response?.data?.message || 'An error occurred while booking',
          error: true,
        });
      },
    );
  };
  // ─────────────────────────────────────────────────────────────────────────

  const renderInputFields = () => {
    const selectedPart = filteredPartValidationRowMapCheckbox.find((row) => row.id === selectedPvRows[0]);

    const barcodeIconHeight = getResponsiveValue({ laptop: 35, fhd: 40, qhd: 50, '4k': 80, '4k_plus': 100 });
    const barcodeIconWidth = getResponsiveValue({ laptop: 75, fhd: 130, qhd: 160, '4k': 180, '4k_plus': 220 });
    const inputMinWidth = getResponsiveValue({ laptop: '400px', fhd: '500px', qhd: '600px', '4k': '700px', '4k_plus': '800px' });

    if (!selectedPart) {
      return (
        <Box className={clsx(styles.scanningInputContainer, { [styles.assemblyLine]: isAssemblyLine })}>
          <Box className={styles.barcodeIconContainer} sx={{ height: barcodeIconHeight, width: barcodeIconWidth }}>
            <img src={darkBarcode} alt="barcode" className={styles.barcodeIcon} />
          </Box>
          <Box className={styles.inputContainer} sx={{ minWidth: inputMinWidth }}>
            <ScanOrEnterBarcodeForm enableAutoEnter={true} inputRef={inputRef} onSubmit={onPartNumberSubmit} placeholder="Part Number Scan" />
          </Box>
        </Box>
      );
    }

    const { genealogyRequired: gr, partNumberScanningRequired: pnsr } = selectedPart;
    let onSubmitHandler = null;
    let shouldScanPartNumber = false;

    if (gr && !pnsr) { onSubmitHandler = onSerialNumberSubmit; }
    else if (pnsr && gr) {
      if (!partFamilyScannedData.isPartNumberScanningDone) { onSubmitHandler = onPartSubmitGenelogyAndPartValidation; shouldScanPartNumber = true; }
      else { onSubmitHandler = onSerialNumberSubmit; }
    } else if (!gr && pnsr) { onSubmitHandler = onPartNumberSubmit; shouldScanPartNumber = true; }

    return (
      <Box className={clsx(styles.scanningInputContainer, { [styles.assemblyLine]: isAssemblyLine })}>
        <Box className={styles.barcodeIconContainer} sx={{ height: barcodeIconHeight, width: barcodeIconWidth }}>
          <img src={themeMode === DARK_MODE ? darkBarcode : scannerIcon} alt="barcode" className={styles.barcodeIcon} />
        </Box>
        <Box className={styles.inputContainer} sx={{ minWidth: inputMinWidth }}>
          <ScanOrEnterBarcodeForm
            enableAutoEnter={gr && pnsr ? false : true}
            inputRef={inputRef}
            onSubmit={onSubmitHandler}
            placeholder={shouldScanPartNumber ? 'Part Number Scan' : 'Serial Number Scan'}
          />
        </Box>
      </Box>
    );
  };

  useEffect(() => { if (!isPopupOpen && inputRef.current) inputRef.current.focus(); }, [isPopupOpen]);

  useEffect(() => {
    tableRows?.forEach((item) => {
      if (item?.quantity === item?.scanJobResults?.length) setAllScanned(true);
      else setAllScanned(false);
    });
  }, [allScanned, tableRows]);

  const isInitialLoad = useRef(true);

  useEffect(() => {
    if (partValidation?.scanJobDetails?.length > 0) {
      if (isInitialLoad.current && currentPartNumber === null) {
        const nonScannedIdx = partValidation.scanJobDetails.findIndex((p) => p.scanJobResults.length !== p.quantity);
        if (nonScannedIdx === -1) setCurrentPartNumber(partValidation.scanJobDetails.length - 1);
        else setCurrentPartNumber(nonScannedIdx);
        isInitialLoad.current = false;
      }
    }
  }, [partValidation]);

  const onMarkUnprocessedSubmit = (reason) => {
    submitMarkUnprocessed(
      partValidation.sequenceNumber,
      { reason },
      () => navigate('/processed-line-item', { state: { fromModalConfirm: true, toTab: 2 } }),
      () => { },
    );
  };

  const onExceptionSubmit = (payload) => {
    raiseExceptionRequest(partValidation?.sequenceNumber, payload, () => { }, () => { });
  };

  const resetState = () => setPartFamilyScannedData(INTIAL_PART_FAMILY_SCAN_DATA);

  const onPartSubmitGenelogyAndPartValidation = (scannedValue, resetBarcodeValue) => {
    const closeMesssageOverlay = showMessageOverlay({ contextText: 'Scanning Part Number.', loading: true });
    const selectedPart = filteredPartValidationRowMapCheckbox.find((row) => row.id === selectedPvRows[0]);
    if (!selectedPart) {
      closeMesssageOverlay();
      showMessageOverlay({ contextText: 'Please select a part to validate', error: true });
      resetBarcodeValue();
      return;
    }
    if (scannedValue === selectedPart.partNumber) {
      setSuccessMessage('Part Number Matched');
      setPartFamilyScannedData((prev) => ({ ...prev, scannedPartNumber: scannedValue, isPartNumberScanningDone: true }));
    } else {
      showMessageOverlay({ contextText: 'Part Number Mismatched', error: true });
    }
    setTimeout(() => { setSuccessMessage(''); resetBarcodeValue(); closeMesssageOverlay(); }, 3000);
  };

  const onPartNumberSubmit = (scannedValue, resetBarcodeValue) => {
    const closeMesssageOverlay = showMessageOverlay({ contextText: 'Scanning Part Number.', loading: true });
    const { skipReason, partNumberScanningRequired: pnsr, genealogyRequired: gr, partPosition } = scanJobDetails[currentPartNumber] || {};

    const selectedRowForPart = filteredPartValidationRowMapCheckbox.find((row) => row.id === selectedPvRows[0]);
    let scanJobResultId = selectedRowForPart?.scanJobResultId;
    if (!scanJobResultId) {
      const cpd = scanJobDetails[currentPartNumber] || {};
      if (cpd?.scanJobResults?.length > 0) scanJobResultId = cpd.scanJobResults[cpd.scanJobResults.length - 1]?.id;
    }

    setPartFamilyScannedData((prev) => ({ ...prev, scannedPartNumber: scannedValue }));

    const successCallback = (data) => {
      setSuccessMessage('Part Matched');
      if (gr && pnsr) {
        setPartFamilyScannedData((prev) => ({ ...prev, scannedPartNumber: scannedValue, isPartNumberScanningDone: true, isErrorInScanningPartNumber: false }));
        setTimeout(() => { setSuccessMessage(''); closeMesssageOverlay(); }, 3000);
      } else {
        setPartFamilyScannedData(INTIAL_PART_FAMILY_SCAN_DATA);
        setTimeout(() => { closeMesssageOverlay(); setSuccessMessage(''); resetBarcodeValue(); }, 3000);
      }
    };

    const errorCallback = (error) => {
      closeMesssageOverlay();
      setSkippedPartNumber(scannedValue);
      setPartFamilyScannedData((prev) => ({ ...prev, isPartNumberScanningDone: false, isErrorInScanningPartNumber: true }));
      showMessageOverlay({ contextText: PART_VALIDATION_ERROR.ERROR_CONTEXT_TEXT + 'Scanning Part Number', info: error.response.data.message, error: true });
      resetBarcodeValue();
    };

    if (!pnsr) return;

    const payload = { familyId, partNumber, scannedPartNumber: scannedValue, scannedSerialNumber: '' };
    const cpd = scanJobDetails[currentPartNumber] || {};
    const hasSJR = cpd?.scanJobResults?.length > 0;
    const isPartProcessed =
      scanJobResultId || cpd.scannedPartNumber || skipReason ||
      selectedRowForPart?.scannedPartNumber || selectedRowForPart?.scannedSerialNumber ||
      selectedRowForPart?.skipReason || selectedRowForPart?.displaySerialNumber || hasSJR;

    const checkAllScanned = () => {
      tableRows.forEach((item) => {
        if (item?.quantity === item?.scanJobResults?.length) setAllScanned(true);
        else setAllScanned(false);
      });
    };

    if (isPartProcessed) {
      revalidatePart(partValidation.sequenceNumber, scanJobResultId, payload, (d) => { successCallback(d); checkAllScanned(); }, errorCallback);
    } else {
      validatePart(partValidation.sequenceNumber, payload, (d) => { successCallback(d); checkAllScanned(); }, errorCallback);
    }
  };

  const onSerialNumberSubmit = (scannedValue, resetBarcodeValue) => {
    const closeMesssageOverlay = showMessageOverlay({ contextText: 'Scanning Serial Number.', loading: true });
    const { skipReason, partNumberScanningRequired: pnsr, genealogyRequired: gr, partPosition, scannedSerialNumber } = scanJobDetails[currentPartNumber] || {};
    const sequenceNumber = partValidation.sequenceNumber;

    const selectedPart = filteredPartValidationRowMapCheckbox.find((row) => row.id === selectedPvRows[0]);
    let scanJobResultId = selectedPart?.scanJobResultId;
    if (!scanJobResultId) {
      const cpd = scanJobDetails[currentPartNumber] || {};
      if (cpd?.scanJobResults?.length > 0) scanJobResultId = cpd.scanJobResults[cpd.scanJobResults.length - 1]?.id;
    }

    const successCallback = (data) => {
      setSuccessMessage('Serial Number Matched');
      setPartFamilyScannedData(INTIAL_PART_FAMILY_SCAN_DATA);
      if (data?.scanJobDetails) setTableRows(getPartFamiliesToValidate(data.scanJobDetails, data.sequenceNumber));
      setTimeout(() => { setSuccessMessage(''); resetBarcodeValue(); closeMesssageOverlay(); }, 3000);
    };

    const errorCallback = (error) => {
      if (tableRows && Array.isArray(tableRows)) {
        setTableRows(tableRows.map((pf) =>
          pf.familyId === familyId && pf.partPosition === partPosition
            ? { ...pf, scannedValue, skippedSerialNumber: scannedValue, displaySerialNumber: scannedValue, isErrorInScanningPartFamily: true }
            : { ...pf }
        ));
      }
      setSkippedSerialNumber(scannedValue);
      closeMesssageOverlay();
      setPartFamilyScannedData((prev) => ({ ...prev, isSerialNumberScanningDone: false, isErrorInScanningSerialNumber: true }));
      resetBarcodeValue();
      showMessageOverlay({ contextText: PART_VALIDATION_ERROR.ERROR_CONTEXT_TEXT + 'Scanning Serial Number', info: error?.response?.data?.message, error: true });
    };

    setPartFamilyScannedData((prev) => ({ ...prev, scannedSerialNumber: scannedValue }));

    let scannedPartNumber;
    if (gr && !pnsr) scannedPartNumber = '';
    else if (gr && pnsr) scannedPartNumber = partNumber;
    else scannedPartNumber = partFamilyScannedData.scannedPartNumber;

    const payload = { familyId, partNumber, scannedPartNumber, scannedSerialNumber: scannedValue };
    const cpd = scanJobDetails[currentPartNumber] || {};
    const hasSJR = cpd?.scanJobResults?.length > 0;
    const selectedRowData = selectedPart || filteredPartValidationRowMapCheckbox.find((r) => r.id === selectedPvRows[0]);
    const isPartProcessed =
      scanJobResultId || scannedSerialNumber || skipReason ||
      selectedRowData?.scannedPartNumber || selectedRowData?.scannedSerialNumber ||
      selectedRowData?.skipReason || selectedRowData?.displaySerialNumber ||
      cpd?.scannedPartNumber || cpd?.scannedSerialNumber || cpd?.skipReason || hasSJR;
    const hasValidSJRId = scanJobResultId && scanJobResultId !== 'undefined' && scanJobResultId !== null;

    const checkAllScanned = () => {
      tableRows.forEach((item) => {
        if (item?.quantity === item?.scanJobResults?.length) setAllScanned(true);
        else setAllScanned(false);
      });
    };

    if (isPartProcessed) {
      revalidatePart(sequenceNumber, scanJobResultId, payload, (d) => { successCallback(d); checkAllScanned(); }, errorCallback);
    } else {
      validatePart(sequenceNumber, payload, (d) => { successCallback(d); checkAllScanned(); }, errorCallback);
    }
  };

  // ── Part validation table state ───────────────────────────────────────────
  const defaultPartValidationColumns = [
    { title: 'Part Name', field: 'name', width: 180, sortable: true, align: 'center' },
    { title: 'Part Number', field: 'number', width: 180, sortable: true, align: 'center' },
    { title: 'Part Description', field: 'description', width: 180, sortable: true, align: 'center' },
    {
      title: 'Qty', field: 'qty', width: 10, align: 'center', columnStyle: { textAlign: 'center' }, sortable: true,
      render: (row) => {
        if (typeof row.qty === 'string' && row.qty.includes('/')) {
          return <Box sx={{ display: 'flex', justifyContent: 'space-evenly' }}><BookingInfoPopup value={row.qty} scanjobResultInfo={row} /></Box>;
        }
        const scanJobResults = row.scanJobResults || [];
        const validatedCount = scanJobResults.filter((r) => !r.skipReason).length;
        const skippedCount = scanJobResults.filter((r) => r.skipReason).length;
        return (
          <Typography sx={{ fontFamily: "'Uni Neue', sans-serif", fontWeight: 800, fontSize: isAssemblyLine ? '40px' : '20px', textAlign: 'center' }}>
            {validatedCount + skippedCount}
          </Typography>
        );
      },
    },
    { title: 'Serial Number', field: 'serial', width: 160, sortable: true, align: 'center' },
    {
      title: 'Status', field: 'status', width: 60, align: 'center',
      render: (row, theme) => {
        const isDark = theme.palette.mode === 'dark';
        const size = scaleValue(28);
        let icon = null, color = '';
        switch (row.status) {
          case 'Validated': icon = <CurlyCheckIcon style={{ width: size, height: size }} />; color = isDark ? '#4CAF50' : '#097F4A'; break;
          case 'Skipped': icon = <SkipNext style={{ width: size, height: size }} />; color = 'rgba(236, 176, 64, 1)'; break;
          default: icon = <EclipseIcon style={{ width: size, height: size }} />; color = isDark ? '#FFFFFF' : '#202020'; break;
        }
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'center' }}>
            {React.cloneElement(icon, { style: { ...icon.props.style, color } })}
          </Box>
        );
      },
    },
  ];

  const [partValidationColumns, setPartValidationColumns] = useState(defaultPartValidationColumns);
  const [partValidationFilters, setPartValidationFilters] = useState({});
  const [partValidationRow, setPartValidationRow] = useState();
  const [filteredPartValidationRow, setFilteredPartValidationRow] = useState([]);

  const filteredPartValidationRowMapCheckbox = useMemo(() => {
    if (!filteredPartValidationRow) return [];
    return filteredPartValidationRow.map((row) => ({ ...row, showCheckBox: true }));
  }, [filteredPartValidationRow]);

  const selectedFamilyIdRef = useRef(null);
  const [skipButtonRow, setSkipButtonRow] = useState(null);

  useEffect(() => {
    const output = updateStatusFromScanJobDetails(scanJobDetails);
    let preservedSelection = [];

    if (selectedFamilyIdRef.current && output?.length > 0) {
      const match = output.find((r) => r.familyId === selectedFamilyIdRef.current);
      if (match?.id) preservedSelection = [match.id];
    }

    if (preservedSelection.length === 0 && selectedPvRows.length > 0 && output?.length > 0) {
      const cur = filteredPartValidationRow.find((r) => r.id === selectedPvRows[0]);
      if (cur?.familyId) {
        selectedFamilyIdRef.current = cur.familyId;
        const match = output.find((r) => r.familyId === cur.familyId);
        if (match?.id) preservedSelection = [match.id];
      }
    }

    setPartValidationRow(output);
    setFilteredPartValidationRow(output);

    if (preservedSelection.length > 0) {
      setSelectedPvRows(preservedSelection);
    } else if (selectedPvRows.length === 0 && output?.length > 0 && isInitialLoad.current) {
      const firstPending = output.find((r) => r.status !== 'Validated' && r.status !== 'Skipped');
      if (firstPending?.id) {
        setSelectedPvRows([firstPending.id]);
        if (firstPending.familyId) selectedFamilyIdRef.current = firstPending.familyId;
      }
    }
  }, [scanJobDetails]);

  const handleSelectionChange = (selectedIds, row) => {
    const latestId = selectedIds.length > 0 ? [selectedIds[selectedIds.length - 1]] : [];
    setSelectedPvRows(latestId);
    setSkipButtonRow(row);
    if (row?.familyId) {
      selectedFamilyIdRef.current = row.familyId;
      const partIdx = scanJobDetails.findIndex((job) => job.familyId === row.familyId);
      if (partIdx !== -1) setCurrentPartNumber(partIdx);
    }
  };

  const torqueTableHeader = useTorqueColumns();
  const [torqueTableRows, setTorqueTableRows] = useState([]);

  useEffect(() => {
    if (!receivePart?.serialNumber) return;
    getProcessValidationDetailsForSerialNumber(
      receivePart.serialNumber,
      (data) => { setTorqueTableRows(data?.machineData?.length > 0 ? data.machineData : []); },
      () => setTorqueTableRows([]),
    );
  }, [receivePart?.serialNumber]);

  // ── Genealogy remaining count ─────────────────────────────────────────────
  let totalRemainingGenologyCount = 0;
  filteredPartValidationRow?.forEach((item) => {
    if (!item?.genealogyRequired) return;
    const total = item?.quantity || 0;
    const sjr = item?.scanJobResults || [];
    const validated = sjr.filter((r) => {
      const hasSerial = r?.scannedSerialNumber != null && r.scannedSerialNumber !== '';
      const hasPart = r?.scannedPartNumber != null && r.scannedPartNumber !== '';
      const notSkipped = r?.skipReason == null;
      return item?.partNumberScanningRequired ? notSkipped && (hasSerial || hasPart) : notSkipped && hasSerial;
    }).length;
    const skipped = sjr.filter((r) => r?.skipReason != null).length;
    totalRemainingGenologyCount += total - (validated + skipped) + skipped;
  });

  const [isExceptionModalOpen, setExceptionModalOpen] = useState(false);

  const bookingSerialNumber = receivePart?.serialNumber || partValidation?.serialNumber || currentLineItem?.serialNumber;

  const partDetailsColumn = [
    {
      width: 300, title: 'Vin', field: 'serialNumber',
      render: (row) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'center' }}>
          <Typography sx={{ fontSize: getResponsiveValue({ laptop: '12px', fhd: '14px', qhd: '16px', '4k': '18px', '4k_plus': '20px' }), fontFamily: "'Uni Neue', sans-serif", fontWeight: 'bold' }}>
            {row.serialNumber}
          </Typography>
        </Box>
      ),
    },
    {
      width: 240, title: 'KSPEC', field: 'parentPartNumber',
      render: (row) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'center' }}>
          <Typography sx={{ fontSize: getResponsiveValue({ laptop: '12px', fhd: '14px', qhd: '16px', '4k': '18px', '4k_plus': '20px' }), fontFamily: "'Uni Neue', sans-serif", fontWeight: 'bold' }}>
            {row.parentPartNumber}
          </Typography>
        </Box>
      ),
    },
    {
      width: 450, title: isPuneJlr() ? 'Model' : 'KSPEC Description', field: isPuneJlr() ? 'model' : 'description',
      render: (row) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'center' }}>
          <Typography sx={{ fontSize: getResponsiveValue({ laptop: '12px', fhd: '14px', qhd: '16px', '4k': '18px', '4k_plus': '20px' }), fontFamily: "'Uni Neue', sans-serif", fontWeight: 'bold' }}>
            {isPuneJlr() ? (currentLineItem?.model || row.parentPartDescription) : row.parentPartDescription}
          </Typography>
        </Box>
      ),
    },
     {
      width: 250, title: 'Lot Number', field: 'lotNumber',
      render: (row) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'center' }}>
          <Typography sx={{ fontSize: getResponsiveValue({ laptop: '12px', fhd: '14px', qhd: '16px', '4k': '18px', '4k_plus': '20px' }), fontFamily: "'Uni Neue', sans-serif", fontWeight: 'bold' }}>
            {currentLineItem?.lotNumber || row.lotNumber}
          </Typography>
        </Box>
      ),
    },
  ];

  const partDetailsRow = [{
    serialNumber: bookingSerialNumber,
    parentPartNumber: partValidation.parentPartNumber,
    parentPartDescription: partValidation.parentPartDescription,
    lotNumber: currentLineItem?.lotNumber || '-',
  }];

  const showMachineTable = torqueTableRows?.length > 0;
  const isDark = themeMode === DARK_MODE;
  const successPct = successLeft / SUCCESS_DISMISS_SEC;

  return (
    <>
      <Box className={styles.bookingContainer}>
        <StyledTable
          title="Current KSPEC"
          columns={partDetailsColumn}
          rows={partDetailsRow}
          forceHeight={isAssemblyLine ? 200 : 100}
          elevated
          showFilter={false}
          showColumns={false}
          sx={{ tableColors: { headerText: '#F8F8F8', headerBackground: isDark ? '#0f172a' : '#0F107B' } }}
        />
      </Box>

      <Box sx={classNames.tableContentContainer}>
        <Box>
          <Grid className={styles.bookingActionBar} sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%', padding: '1vh 0' }}>
            <TableActionsBar
              leftContent={<GenealogyChip label="Genealogy Pending" value={totalRemainingGenologyCount} success={totalRemainingGenologyCount > 0} />}
              showDefactive={!!bookingSerialNumber && !!partValidation?.sequenceNumber}
              onDefectClick={openDefectModal}
              onTakeOutClick={openTakeOutModal}
              onExceptionClick={() => setExceptionModalOpen(true)}
              onViewReportClick={openReportModal}
              showTakeOut showColumns showFilter showViewReport showException showExport={false}
              columns={partValidationColumns}
              defaultColumns={defaultPartValidationColumns}
              rows={partValidationRow || []}
              onFilterChange={(filters) => setPartValidationFilters(filters)}
              onColumnChange={(updatedColumns) => setPartValidationColumns(updatedColumns)}
            />
          </Grid>

          <Grid container spacing={2} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'stretch', height: '40vh', minHeight: 330 }}>
            <Grid item xs={showMachineTable ? 8 : 12} sx={{ display: 'flex', flexDirection: 'column' }}>
              <StyledTable
                columns={partValidationColumns}
                rows={filteredPartValidationRowMapCheckbox || []}
                filters={partValidationFilters}
                showCheckBox elevated
                selectedRows={selectedPvRows}
                onSelectionChange={handleSelectionChange}
                onHandleRowClick={resetState}
                sx={{ flex: 1, height: '100%', tableColors: { headerText: '#F8F8F8', headerBackground: isDark ? '#0f172a' : '#0F107B' } }}
              />
            </Grid>
            {showMachineTable && (
              <Grid item xs={4} sx={{ display: 'flex', flexDirection: 'column' }}>
                <StyledTable
                  columns={torqueTableHeader}
                  rows={torqueTableRows}
                  selectall={false} elevated paginated={true}
                  sx={{ flex: 1, height: '100%', tableColors: { headerText: '#F8F8F8', headerBackground: isDark ? '#0f172a' : '#0F107B' } }}
                />
              </Grid>
            )}
          </Grid>
        </Box>

        {modeType === 'Manual Mode' ? (
          <BasicTable
            listId="genealogy-page"
            rows={tableRows || []}
            columns={[...PVnG_DEFAULT_COLUMNS]}
            defaultColumns={[...PVnG_DEFAULT_COLUMNS]}
            showCheckBox
            tableStyles={{ paper: classNames.paper, tableContainer: classNames.tableContentContainer }}
          >
            {(selectedRows) => (
              <>
                <Divider />
                <Grid container item justifyContent="space-between" alignItems="bottom" paddingX={3} paddingBottom={2}>
                  <Grid xs container>
                    <UpdatePartValidationGenealogy
                      themeMode={themeMode}
                      selectedRows={selectedRows}
                      setAllScanned={setAllScanned}
                      revalidatePart={revalidatePart}
                      validatePart={validatePart}
                      tableRows={tableRows}
                      setTableRows={setTableRows}
                      partValidation={partValidation}
                      isAssemblyLine={isAssemblyLine}
                    />
                  </Grid>
                </Grid>
              </>
            )}
          </BasicTable>
        ) : (
          <Grid container paddingX={1} paddingY={3} alignItems="flex-end" sx={{ height: 'auto', minHeight: '20vh' }}>
            <Grid item sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
              <SkipPart
                skippedSerialNumber={skipButtonRow?.name}
                skippedPartNumber={skipButtonRow?.number}
                selectedRowForSkip={skipButtonRow}
                successCallback={partSkipSuccessCallback}
                errorCallback={partSkipErrorCallback}
                currentPartNumber={currentPartNumber}
                disableBtn={selectedPvRows.length === 0}
                hoverMessage={selectedPvRows.length === 0 ? 'Please select a part to skip' : ''}
              />
              <TakeOut
                partDetails={{ serialNumber: bookingSerialNumber, sequenceNumber: partValidation?.sequenceNumber }}
                redirectToNextScreen={redirectToHomeScreen}
                btnStyles={{
                  height: 56, px: 4, borderRadius: '12px',
                  background: 'linear-gradient(90deg, #CB1D49 0%, #C71616 100%)',
                  fontSize: `${scaleValue(18)}px`,
                  '& svg': { display: 'none' },
                  '&:hover': { background: 'linear-gradient(90deg, #CB1D49 0%, #C71616 100%) !important' },
                }}
              />
              <MoveToMr
                partDetails={{
                  ...partValidation,
                  serialNumber: bookingSerialNumber || partValidation?.serialNumber,
                  sequenceNumber: partValidation?.sequenceNumber || bookingSerialNumber,
                }}
              />
            </Grid>

            <Grid item xs />

            <Grid item sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', alignItems: 'center', width: '30%' }}>
              {!successMessage && renderInputFields()}
            </Grid>

            <Grid item xs />

            <Grid item>
              <GradientButton
                sx={{
                  fontSize: `${scaleValue(28)}px !important`,
                  fontWeight: 'bold !important',
                  '&.Mui-disabled': {
                    background: 'linear-gradient(90deg, #d1d1d1 0%, #7a7a79 100%) !important',
                    color: '#FFFFFF !important',
                    opacity: 0.7,
                  },
                }}
                disabled={totalRemainingGenologyCount > 0}
                gradient="linear-gradient(90deg, #435C42 0%, #619C31 100%)"
                onClick={() => setIsBookingConfirmOpen(true)}
              >
                <CarRepairRounded sx={{ fontSize: `${scaleValue(28)}px !important` }} />
                Book
              </GradientButton>
            </Grid>
          </Grid>
        )}
      </Box>

      {/* ── Booking modal: Confirm → Success only (no countdown/loader) ───── */}
      {(isBookingConfirmOpen || bookingPhase !== 'idle') && (
        <Box sx={{
          position: 'fixed', top: 0, left: 0,
          width: '100vw', height: '100vh',
          backgroundColor: 'rgba(0, 0, 0, 0.55)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          zIndex: 9999,
        }}>
          <Box sx={{
            background: isDark ? '#1e293b' : '#ffffff',
            borderRadius: '16px',
            padding: '40px 48px',
            minWidth: '440px',
            textAlign: 'center',
            boxShadow: '0 12px 40px rgba(0,0,0,0.25)',
          }}>

            {/* ── Phase 1: Confirm dialog ── */}
            {isBookingConfirmOpen && bookingPhase === 'idle' && (
              <>
                <Typography variant="h5" fontWeight={700} mb={1}
                  sx={{ color: isDark ? '#f1f5f9' : '#0F107B' }}>
                  Confirm Booking
                </Typography>
                <Typography variant="body1" mb={4}
                  sx={{ color: isDark ? '#cbd5e1' : '#555', fontSize: '1.1rem' }}>
                  Proceed with booking this sequence?
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
                  <Button
                    variant="outlined"
                    onClick={() => setIsBookingConfirmOpen(false)}
                    sx={{
                      minWidth: 130, fontWeight: 600,
                      borderColor: '#e53935', color: '#e53935',
                      '&:hover': { borderColor: '#b71c1c', color: '#b71c1c', backgroundColor: 'rgba(229,57,53,0.06)' },
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="contained"
                    onClick={handleBookingConfirm}
                    sx={{
                      minWidth: 130, fontWeight: 600,
                      background: 'linear-gradient(90deg, #435C42 0%, #619C31 100%)',
                      '&:hover': { background: 'linear-gradient(90deg, #364a35 0%, #4e7d27 100%)' },
                    }}
                  >
                    Confirm
                  </Button>
                </Box>
              </>
            )}

            {/* ── Phase 2: 10s Success auto-dismiss ── */}
            {bookingPhase === 'success' && (
              <>
                <Box sx={{
                  width: 72, height: 72, borderRadius: '50%',
                  background: '#EAF3DE',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  margin: '0 auto 20px',
                }}>
                  <svg width="36" height="36" viewBox="0 0 36 36" fill="none"
                    stroke="#3B6D11" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="7,19 15,27 29,11" />
                  </svg>
                </Box>

                <Typography variant="h5" fontWeight={700} mb={0.75}
                  sx={{ color: isDark ? '#a3e6a3' : '#27500A' }}>
                  Booking Confirmed
                </Typography>
                <Typography variant="body2" mb={3}
                  sx={{ color: isDark ? '#94a3b8' : '#555' }}>
                  {partValidation.serialNumber || bookingSerialNumber} booked successfully.
                </Typography>

                {/* 10s dismiss progress bar */}
                <Box sx={{
                  width: '100%', height: 5, borderRadius: '3px',
                  background: isDark ? '#334155' : '#e2e8f0',
                  overflow: 'hidden', mb: 1,
                }}>
                  <Box sx={{
                    height: '100%', borderRadius: '3px',
                    background: '#619C31',
                    width: `${(successPct * 100).toFixed(1)}%`,
                    transition: 'width 1s linear',
                  }} />
                </Box>
                <Typography variant="caption" sx={{ color: isDark ? '#64748b' : '#aaa' }}>
                  Closing in {successLeft}s
                </Typography>
              </>
            )}

          </Box>
        </Box>
      )}
      {/* ─────────────────────────────────────────────────────────────────── */}

      <AllPartsModal
        open={isDetailsModalOpen}
        handleClose={closeDetailsModal}
        rows={[partValidation]}
        columns={[...partDetailsColumn]}
        defaultColumns={[...partDetailsColumn]}
        title="Current KSPEC"
        subtitle="Key Attributes"
        tableStyles={{ paper: classNames.paper }}
        scannableParts={scanJobDetails}
      />

      <DefectModal
        open={isDefectModalOpen}
        handleClose={closeDefectModal}
        partDetailsRow={partDetailsRow}
        partDetails={{ serialNumber: bookingSerialNumber, sequenceNumber: partValidation.sequenceNumber }}
      />

      <ReasonAndRemarkModal
        open={isTakeOutModalOpen}
        nameofModal="Take Out"
        handleClose={closeTakeOutModal}
        data={{ vin: partValidation.serialNumber || 'N/A', KSPEC: partValidation.parentPartNumber || 'N/A' }}
        dropDown={['Duplicate serial no.', 'Damaged part', 'Incorrect assembly']}
        leftTitle="VIN"
        rightTitle="KSPEC"
        handleSubmit={onMarkUnprocessedSubmit}
      />

      <ExceptionModal
        open={isExceptionModalOpen}
        handleClose={() => setExceptionModalOpen(false)}
        handleSubmit={onExceptionSubmit}
      />

      <RescanModal
        open={isReportModalOpen}
        handleClose={closeReportModal}
        rows={[partValidation]}
        columns={[...partDetailsColumn]}
        defaultColumns={[...partDetailsColumn]}
        title="VALIDATION REPORT"
        subtitle="Key Attributes"
        tableStyles={{ paper: classNames.paper }}
        scannableParts={scanJobDetails}
        torqueData={torqueTableRows}
      />
    </>
  );
};

export default connect(mapStateToProps, mapDispatchToProps)(BookingValidationStages);